import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Cookie, 
  X, 
  Settings, 
  Check, 
  ExternalLink,
  Shield,
  BarChart3,
  Zap
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCookieConsentStore } from '@/stores/cookieConsentStore'
import { cn } from '@/lib/utils'
import { useNavigate } from 'react-router-dom'

export function CookieConsentBanner() {
  const navigate = useNavigate()
  const { consent, hasShownBanner, setConsent, acceptAll, rejectAll } = useCookieConsentStore()
  const [showDetails, setShowDetails] = useState(false)
  const [customConsent, setCustomConsent] = useState({
    necessary: true,
    analytics: false,
    performance: false
  })

  // Don't show banner if user has already made a choice
  if (hasShownBanner && consent) {
    return null
  }

  const handleCustomSave = () => {
    setConsent(customConsent)
  }

  const toggleCustomConsent = (type: 'analytics' | 'performance') => {
    setCustomConsent(prev => ({
      ...prev,
      [type]: !prev[type]
    }))
  }

  const handlePrivacyPolicy = () => {
    navigate('/privacy-policy')
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg border-t border-gray-200 dark:border-gray-700 shadow-lg"
      >
        <div className="container mx-auto px-4 py-6">
          {!showDetails ? (
            // Simple Banner
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Cookie className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                    We use cookies to enhance your experience
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    We use essential cookies for functionality and optional cookies for analytics and performance monitoring. 
                    You can choose which cookies to accept.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-wrap items-center gap-2 lg:flex-shrink-0">
                <Button
                  onClick={handlePrivacyPolicy}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  Privacy Policy
                </Button>
                <Button
                  onClick={() => setShowDetails(true)}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  <Settings className="h-3 w-3 mr-1" />
                  Customize
                </Button>
                <Button
                  onClick={rejectAll}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  Reject All
                </Button>
                <Button
                  onClick={acceptAll}
                  size="sm"
                  className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white text-xs"
                >
                  <Check className="h-3 w-3 mr-1" />
                  Accept All
                </Button>
              </div>
            </div>
          ) : (
            // Detailed Settings
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                    <Cookie className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Cookie Preferences
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Choose which cookies you want to accept
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowDetails(false)}
                  variant="ghost"
                  size="sm"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="grid gap-4">
                {/* Necessary Cookies */}
                <div className="flex items-start gap-4 p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                  <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        Necessary Cookies
                      </h4>
                      <div className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-medium rounded">
                        Always Active
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                      These cookies are essential for the website to function properly. They enable basic features like 
                      page navigation, form submissions, and access to secure areas.
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Examples: Session tokens, user preferences, authentication
                    </p>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start gap-4 p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        Analytics Cookies
                      </h4>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={customConsent.analytics}
                          onChange={() => toggleCustomConsent('analytics')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                      These cookies help us understand how visitors interact with our website by collecting 
                      and reporting information anonymously.
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Examples: Vercel Analytics, page views, user behavior patterns
                    </p>
                  </div>
                </div>

                {/* Performance Cookies */}
                <div className="flex items-start gap-4 p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                  <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        Performance Cookies
                      </h4>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={customConsent.performance}
                          onChange={() => toggleCustomConsent('performance')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-orange-600"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                      These cookies allow us to monitor website performance and improve loading times 
                      and overall user experience.
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Examples: Vercel Speed Insights, performance metrics, load times
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  onClick={handlePrivacyPolicy}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  Privacy Policy
                </Button>
                <div className="flex-1"></div>
                <Button
                  onClick={rejectAll}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  Reject All
                </Button>
                <Button
                  onClick={handleCustomSave}
                  size="sm"
                  className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white text-xs"
                >
                  <Check className="h-3 w-3 mr-1" />
                  Save Preferences
                </Button>
                <Button
                  onClick={acceptAll}
                  size="sm"
                  className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white text-xs"
                >
                  Accept All
                </Button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  )
}
